<template>
  <fieldset class="demo-module-picker" data-test="demo-module-picker" :disabled="disabled">
    <legend class="demo-module-picker__legend">{{ t('demoModules.legend') }}</legend>
    <div class="demo-module-picker__chips">
      <!-- One chip per module: a pill whose face is a span and whose control is a REAL
           checkbox laid over it (invisible, the size of the chip), so a click, the
           keyboard and a test's `.check()` all land on the input itself. A locked module
           is checked and disabled; its "booked" reason rides the chip as a tooltip and as
           text a screen reader gets. -->
      <label
        v-for="m in offer"
        :key="m.key"
        class="demo-chip"
        :class="{ 'demo-chip--locked': m.locked, 'demo-chip--picked': !m.locked && chosen.includes(m.key), 'demo-chip--off': disabled && !m.locked }"
        data-test="demo-module-option"
        :data-module="m.key"
        :data-locked="m.locked ? 'true' : undefined"
        :title="m.locked ? t('demoModules.booked') : undefined"
      >
        <input v-model="chosen" class="demo-chip__input" type="checkbox" :value="m.key" :disabled="disabled || m.locked" />
        <span class="demo-chip__face">
          <LumiIcon :name="m.locked ? 'privacy' : chosen.includes(m.key) ? 'confirm' : 'add'" class="demo-chip__icon" />
          <span class="demo-chip__label">{{ moduleName(m.key) }}</span>
          <span v-if="m.locked" class="demo-chip__sr">{{ t('demoModules.booked') }}</span>
        </span>
      </label>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { LumiIcon } from '@lumi/ui';
import type { DemoOfferModule } from '../composables/useOnboardingStore';
import { USER_API_TO_LUMI } from '../utils/moduleSwitcher';

// The demo's module picker (ADR 0083 decision 1, docs/contracts/demo-cast.v5.md
// § "The module set"). Every demo module the status offers, in seed order; a
// locked one (People, or a module the tenant booked) is checked and cannot be
// left out. The model is always the chosen keys in offer order with every locked
// key in it — whatever a caller seeds it with — so the request carries a set the
// server accepts. The server validates the same rule (400 otherwise).
const props = defineProps<{
  offer: DemoOfferModule[];
  disabled?: boolean;
}>();
const model = defineModel<string[]>({ required: true });

const { t } = useI18n();

function normalized(keys: readonly string[]): string[] {
  return props.offer.filter((m) => m.locked || keys.includes(m.key)).map((m) => m.key);
}
const chosen = computed<string[]>({
  get: () => normalized(model.value),
  set: (next) => {
    model.value = normalized(next);
  },
});
// A seed that is not yet a valid set (an offer that arrived after it, a locked
// key missing) is corrected in the model itself, not only on screen.
watch(
  () => [props.offer, model.value] as const,
  () => {
    const next = normalized(model.value);
    if (next.join() !== model.value.join()) model.value = next;
  },
  { immediate: true, deep: true },
);

// The product names the launcher and the bar's task chips use.
function moduleName(key: string): string {
  const lumiKey = USER_API_TO_LUMI[key];
  return lumiKey ? t(`lumiModules.${lumiKey}.name`) : key;
}
</script>

<style scoped>
.demo-module-picker {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
}
.demo-module-picker__legend {
  padding: 0;
  margin-bottom: 10px;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.demo-module-picker__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* A chip is three states of one pill: locked (booked, fixed in), picked (added by the
   admin, lavender) and open (dashed, an invitation to add). */
.demo-chip {
  position: relative;
  display: inline-flex;
  max-width: 100%;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}
.demo-chip__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.demo-chip__face {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: var(--radius-pill);
  border: 1px dashed color-mix(in srgb, var(--text-tertiary) 50%, var(--surface-card));
  background: var(--surface-card);
  color: var(--text-secondary);
  transition: background-color var(--dur-fast) var(--ease-standard), border-color var(--dur-fast) var(--ease-standard);
}
.demo-chip:not(.demo-chip--locked):hover .demo-chip__face {
  border-color: var(--text-tertiary);
}
.demo-chip--picked .demo-chip__face {
  border: 1px solid var(--info-soft);
  background: var(--selected-bg);
  color: var(--info-text);
}
.demo-chip--locked .demo-chip__face {
  border: 1px solid var(--pill-ring);
  background: color-mix(in srgb, var(--text-heading) 7%, var(--surface-card));
  color: var(--text-body);
}
.demo-chip--locked .demo-chip__input {
  cursor: default;
}
.demo-chip--off .demo-chip__input {
  cursor: not-allowed;
}
.demo-chip--off .demo-chip__face {
  opacity: 0.6;
}
.demo-chip__input:focus-visible + .demo-chip__face {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.demo-chip__icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.demo-chip__label {
  min-width: 0;
  overflow-wrap: anywhere;
}
/* The "booked" reason for assistive tech; the sighted get it as the chip's tooltip. */
.demo-chip__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
  .demo-chip__face {
    transition: none;
  }
}
</style>
